import { VisibilityOutlined } from "@mui/icons-material";
import FileSuccess from "assets/img/svg/fileSuccess";
import Import from "assets/img/svg/import";
import ModalBox from "components/Modal";
import PreviewDeclaration from "components/shared/PreviewDeclaration";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import type { BeneficiaryLine, LastStepStatus, Quarter } from "components/URSSAFModal";
import { formatLocaleDateString } from "helpers/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDeclaration } from "store/contributionStore";

import variables from "../../../../assets/styles/_variables.module.scss";
import CorrectionTable from "../CorrectionTable";
import { quarterList } from "../ImportFile";
import stylesImport from "../ImportFile/style.module.scss";

export interface BasicPayloadError {
    fileLine: number;
    field: string;
    message: string;
}

interface LastStepProps {
    onSubmit: (status: LastStepStatus, data: string | BeneficiaryLine[]) => void;
    status: LastStepStatus;
    contribution?: IDeclaration;
    quarter: Quarter;
    onDownload: () => void;
    onReset: () => void;
    onDeclared: () => void;
    extraData: { errors: BasicPayloadError[]; lines: BeneficiaryLine[] };
}

export default function LastStep(props: LastStepProps) {
    const [t] = useTranslation();
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [hasDownloaded, setHasDownloaded] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const handleDownload = () => {
        props.onDownload();
        setHasDownloaded(true);
    };

    const handleDeclare = () => {
        if (hasDownloaded) props.onDeclared();
        else setShowModal(true);
    };

    const handleDeclareAndClose = () => {
        setShowModal(false);
        setHasDownloaded(true);
        props.onDeclared();
    };

    const successComponents = (
        <div className={stylesImport.Importer}>
            <PreviewDeclaration
                showModal={showPreviewModal}
                onClose={() => setShowPreviewModal(false)}
                contributionId={props.contribution?.id}
            />
            <ModalBox open={showModal} onClose={() => setShowModal(false)} mainTitle={t("Declare.LastStep.ModalTitle")}>
                <div>
                    {t("Declare.LastStep.ModalContent")}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "8px",
                            marginTop: "16px",
                        }}
                    >
                        <StyledButton buttonType="secondary" onClick={() => setShowModal(false)}>
                            {t("Declare.LastStep.No")}
                        </StyledButton>
                        <StyledButton buttonType="primary" onClick={handleDeclareAndClose}>
                            {t("Declare.LastStep.Yes")}
                        </StyledButton>
                    </div>
                </div>
            </ModalBox>
            <div className="step-header">
                <h1>{t("Declare.LastStep.Title")}</h1>
                <p
                    style={{
                        textAlign: "center",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: variables["foreground"],
                    }}
                >
                    {t("Declare.LastStep.Subtitle", {
                        date: formatLocaleDateString({ date: new Date(props.contribution?.endDate ?? "") }),
                    })}
                </p>
            </div>

            <div className={stylesImport["import-steps"]}>
                <div className={stylesImport["import-step"]}>
                    <div className={stylesImport["import-step-title"]}>{t("Declare.Step")} 1</div>
                    <p>{t("Declare.LastStep.Step1")}</p>
                    <a onClick={handleDownload}>
                        <div className={stylesImport["import-step-example-file-large"]}>
                            <div>
                                <FileSuccess color="#000" />
                                {t("Declare.LastStep.DownloadFile", {
                                    quarter: t(`Declare.${quarterList[props.quarter]}`),
                                })}
                            </div>
                            <div>
                                <Import />
                            </div>
                        </div>
                    </a>
                    <StyledButton
                        buttonType="secondary"
                        sx={{
                            padding: "4px 16px !important",
                            backgroundColor: "transparent !important",
                        }}
                        onClick={() => setShowPreviewModal(true)}
                    >
                        <VisibilityOutlined fontSize="small" />
                        {t("Declare.LastStep.Preview")}
                    </StyledButton>
                </div>
                <div className={stylesImport["import-step"]}>
                    <div className={stylesImport["import-step-title"]}>{t("Declare.Step")} 2</div>
                    <p>{t("Declare.LastStep.Step2")}</p>
                </div>
            </div>

            <div className={stylesImport["last-step-actions"]}>
                <StyledButton buttonType="secondary" onClick={props.onReset}>
                    {t("Declare.LastStep.Retry")}
                </StyledButton>
                <StyledButton buttonType="primary" onClick={handleDeclare}>
                    {t("Declare.LastStep.Save")}
                </StyledButton>
            </div>
        </div>
    );

    switch (props.status) {
        case "infos":
            return <></>;
        case "corrections":
            return (
                <CorrectionTable
                    lines={props.extraData.lines}
                    errors={props.extraData.errors}
                    onSubmit={(payload: any) => props.onSubmit(props.status, payload)}
                />
            );
        case "PaymentDateOutOfRange":
            return (
                <div style={{ margin: "32px" }}>
                    <p style={{ textAlign: "center" }}>
                        {t("Declare.LastStep.PaymentOutOfRange")}
                        <br />
                        {t("Declare.LastStep.CorrectThem")}
                    </p>
                    <StyledButton buttonType="secondary" onClick={props.onReset}>
                        {t("Declare.LastStep.Retry")}
                    </StyledButton>
                </div>
            );
        case "loading":
            return (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                    <Spinner />
                    <h3>{t("Declare.LastStep.Analyzing")}</h3>
                </div>
            );
        case "once-only":
            return <></>;
        case "success":
            return successComponents;
    }
}
